import { useMemo } from 'react'

import {
  IconArrowBackUp,
  IconBug,
  IconBuilding,
  IconHome,
  IconSwitch3,
} from '@tabler/icons-react'
import { Tooltip, message } from 'antd'
import { useRouter } from 'next/router'
import CopyToClipboard from 'react-copy-to-clipboard'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'
import { PopoverDivider, PopoverItem } from '../shared-components/BUI/Popover'
import { Flexbox } from '../shared-components/Flexbox'

import { useAuth } from '@/context/authContext'
import { useProject } from '@/context/projectContext'
import { useSupport } from '@/context/supportContext'
import { useSwitchBuilderModal } from '@/context/switchBuilderContext'
import { useUser } from '@/context/userContext'
import { useHasRoles } from '@/hooks/useHasRoles'
import { Role } from '@/modules/adminUsers/roles'
import { getUserName } from '@/utils/getName'

import { buildPassTheme } from 'src/styles/theme'

const PopoverSubscript = styled.span`
  ${({ theme }) => theme.font.caption02}
  font-size: 8px;
  padding-left: 1rem;
`

const SuperAdminAction = styled(PopoverItem)`
  color: ${({ theme }) => theme.colour.primary._600};
  & svg {
    font-size: 1rem;
    color: ${({ theme }) => theme.colour.primary._600};
  }
`

export const SuperAdminSupportOptions = () => {
  const isSuperAdmin = useHasRoles(Role.SuperAdmin)

  const { roles, user: authUser } = useAuth()
  const { asPath } = useRouter()
  const { user, builder } = useUser()
  const { isProjectSelected, project } = useProject()
  const { isInSupportMode, resetToOriginalBuilder } = useSupport()

  const { openSwitchBuilderModal } = useSwitchBuilderModal()
  const debugJson = useMemo(() => {
    return JSON.stringify(
      {
        user: {
          id: user?.id,
          adminUserId: user?.adminUser?.id,
          name: getUserName(user),
          email: user?.email,
          authUserId: authUser.externalId,
          roles,
        },
        builder: {
          id: builder?.id,
          name: builder?.company.name,
          authOrgId: builder?.clerkOrganizationId,
        },
        project: {
          id: project?.id,
          name: project?.name,
          status: project?.status,
        },
        currentUrl: asPath,
      },
      null,
      2
    )
  }, [asPath, builder, project, user, roles, authUser])

  if (!isSuperAdmin) return null

  return (
    <>
      <PopoverDivider />
      <PopoverSubscript>Support</PopoverSubscript>

      <Tooltip title="It's Morphin Time!">
        <SuperAdminAction onClick={openSwitchBuilderModal}>
          <IconSwitch3 size={18} />
          Switch Builder
        </SuperAdminAction>
      </Tooltip>

      <PopoverItem>
        <Flexbox gap="0.5rem">
          {isInSupportMode && (
            <Tooltip title="Reset builder">
              <Button
                size="small"
                variant="primary-text"
                shape="circle"
                icon={
                  <IconArrowBackUp
                    size={16}
                    color={buildPassTheme.colour.primary._600}
                  />
                }
                onClick={resetToOriginalBuilder}
              />
            </Tooltip>
          )}

          <Tooltip title="Copy Builder Id">
            <CopyToClipboard
              text={builder?.id}
              onCopy={() => message.success('Builder Id copied to clipboard')}
            >
              <Button
                size="small"
                variant="primary-text"
                shape="circle"
                icon={
                  <IconBuilding
                    size={16}
                    color={buildPassTheme.colour.primary._600}
                  />
                }
              />
            </CopyToClipboard>
          </Tooltip>

          {isProjectSelected && (
            <Tooltip title="Copy Project Id">
              <CopyToClipboard
                text={project?.id}
                onCopy={() => message.success('Project Id copied to clipboard')}
              >
                <Button
                  size="small"
                  variant="primary-text"
                  shape="circle"
                  icon={
                    <IconHome
                      size={16}
                      color={buildPassTheme.colour.primary._600}
                    />
                  }
                />
              </CopyToClipboard>
            </Tooltip>
          )}

          <Tooltip title="Copy Debug Info">
            <CopyToClipboard
              text={debugJson}
              onCopy={() => message.success('Debug info copied to clipboard')}
            >
              <Button
                size="small"
                variant="primary-text"
                shape="circle"
                icon={
                  <IconBug
                    size={16}
                    color={buildPassTheme.colour.primary._600}
                  />
                }
              />
            </CopyToClipboard>
          </Tooltip>
        </Flexbox>
      </PopoverItem>
    </>
  )
}
